/**
 * [비즈니스 목적]
 * 테넌트 사용자 및 일반 사용자를 위한 기본 사이드바 네비게이션
 *
 * [워크플로우]
 * 1. 메뉴 아이템 렌더링 (아이콘, 텍스트, 경로)
 * 2. 현재 경로에 따라 활성 메뉴 하이라이트
 * 3. 확장 가능한 서브메뉴 지원 (Collapse)
 * 4. 메뉴 클릭 시 페이지 이동
 *
 * [주요 기능]
 * 1. 네비게이션 메뉴
 *    - 파일 온톨로지 현황
 *    - 데이터 시스템 접속정보 관리
 *    - 고객사 사용자 관리
 *    - Settings
 *
 * 2. 확장 가능한 메뉴 그룹
 *    - expandedItems 상태로 관리
 *    - ExpandMore/ExpandLess 아이콘으로 상태 표시
 *
 * 3. 활성 메뉴 하이라이트
 *    - 현재 경로와 일치하는 메뉴 강조 표시
 *    - 서브메뉴도 상위 메뉴 하이라이트
 *
 * [Props]
 * - onClose: 모바일에서 메뉴 선택 시 Drawer 닫기 핸들러
 *
 * [상태 관리]
 * - expandedItems: 확장된 메뉴 그룹 배열
 *
 * [스타일링]
 * - 선택된 메뉴: primary.main 배경, 흰색 텍스트
 * - 호버: primary.dark 배경
 * - 서브메뉴: primary.light 배경, 들여쓰기 4 유닛
 *
 * [사용 예시]
 * <Sidebar onClose={handleDrawerToggle} />
 */

import React from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Collapse,
  Chip
} from '@mui/material';
import {
  Storage,
  Description,
  Group,
  Category,
  Settings,
  FolderOpen,
  Transform,
  SmartToy,
  Code,
  ExpandLess,
  ExpandMore,
  VpnKey,
  People
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';

const menuItems = [
  {
    text: '파일 온톨로지',
    icon: <FolderOpen />,
    path: '/admin/ontology',
    expandable: true,
    subItems: [
      {
        text: '파일 온톨로지 현황',
        icon: <Description />,
        path: '/admin/ontology'
      },
    ]
  },
  {
    text: '데이터 시스템 접속정보 관리',
    icon: <VpnKey />,
    path: '/admin/connection-info'
  },
  {
    text: '고객사 사용자 관리',
    icon: <People />,
    path: '/admin/user-management'
  },
  {
    text: 'Settings',
    icon: <Settings />,
    path: '/admin/settings'
  }
];

const Sidebar = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState(['파일 온톨로지']);

  const handleNavigation = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const handleExpandClick = (itemText) => {
    setExpandedItems(prev =>
      prev.includes(itemText)
        ? prev.filter(item => item !== itemText)
        : [...prev, itemText]
    );
  };

  const isSelected = (path) => {
    if (path === '/dashboard' && location.pathname === '/') {
      return true;
    }
    return location.pathname === path;
  };

  const isSubItemSelected = (parentPath, subPath) => {
    return location.pathname === subPath || 
           (location.pathname.startsWith(parentPath) && subPath === parentPath);
  };

  return (
    <Box>
      {/* Logo/Title */}
      <Toolbar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Category color="primary" />
          <Typography variant="h6" noWrap component="div" color="primary">
            Ontology
          </Typography>
        </Box>
      </Toolbar>
      
      <Divider />

      {/* Navigation Menu */}
      <List>
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            <ListItem disablePadding>
              <ListItemButton
                selected={!item.expandable && isSelected(item.path)}
                onClick={() => {
                  if (item.expandable) {
                    handleExpandClick(item.text);
                  } else {
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText primary={item.text} />
                {item.expandable && (
                  expandedItems.includes(item.text) ? <ExpandLess /> : <ExpandMore />
                )}
              </ListItemButton>
            </ListItem>

            {/* Sub Items */}
            {item.expandable && (
              <Collapse in={expandedItems.includes(item.text)} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems?.map((subItem) => (
                    <ListItem key={subItem.text} disablePadding>
                      <ListItemButton
                        selected={isSubItemSelected(item.path, subItem.path)}
                        onClick={() => handleNavigation(subItem.path)}
                        sx={{
                          pl: 4,
                          '&.Mui-selected': {
                            backgroundColor: 'primary.light',
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                            '&:hover': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          {subItem.icon}
                        </ListItemIcon>
                        <ListItemText primary={subItem.text} />
                        {subItem.badge && (
                          <Chip
                            label={subItem.badge}
                            size="small"
                            color={isSubItemSelected(item.path, subItem.path) ? "default" : "primary"}
                            variant={isSubItemSelected(item.path, subItem.path) ? "filled" : "outlined"}
                            sx={{ 
                              fontSize: '0.75rem',
                              height: 20,
                              '& .MuiChip-label': {
                                px: 1
                              }
                            }}
                          />
                        )}
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
    </Box>
  );
};

export default Sidebar;